import React, { useContext, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { View, Image, Platform } from 'react-native';
import {
  Card, Chip, IconButton, Menu, Text,
} from 'react-native-paper';
import { useMutation, useQuery } from '@apollo/client';
import { NativeBaseProvider } from 'native-base';
import * as ImagePicker from 'expo-image-picker';
import { addDays } from 'date-fns';
import { AuthContext } from '../../context/AuthContext';
import { FindOneEvent } from '../../gql/event/query';
import { styles } from '../Common.style';
import {
  EVENT_LEVEL, EVENT_STATUS, EVENT_TYPE, ROLE_NAMES,
} from '../../utils/constant';
import EventTabs from './EventTabs';
import { UpdateEvent } from '../../gql/event/mutation';
import { ModalContext } from '../../context/ModalContext';
import CreateAchievementModal from '../../modals/ManageAchievements/CreateAchievementModal';
import ManageTripModal from '../../modals/ManageTrip/ManageTrip';
import LoadingIndicator from '../../components/commons/LoadingIndicator';

function Event() {
  const route = useRoute();
  const { authState } = useContext(AuthContext);
  const {
    displayCreateAchievementModal,
    displayManageTripModal,
  } = useContext(ModalContext);

  const [visibleEventType, setVisibleEventType] = useState(false);
  const [visibleEventLevel, setVisibleEventLevel] = useState(false);
  const [visibleEventStatus, setVisibleEventStatus] = useState(false);

  const isSuperAdmin = authState.role === ROLE_NAMES.SUPERADMIN;

  const {
    data: oneEventQuery,
    loading: oneEventQueryLoading,
    error: oneEventQueryError,
  } = useQuery(FindOneEvent, {
    variables: {
      id: Number(route?.params?.eventId),
    },
  });
  const event = oneEventQuery?.findOneEvent;

  const [updateEvent] = useMutation(UpdateEvent);

  const saveEventCallback = async (data) => {
    if (data) {
      await updateEvent({
        variables: {
          id: Number(event.id),
          type: data.type
            ? Object.entries(EVENT_TYPE).find((e) => e[1] === data.type)[0]
            : undefined,
          level: data.level
            ? Object.entries(EVENT_LEVEL).find((e) => e[1] === data.level)[0]
            : undefined,
          status: data.status
            ? Object.entries(EVENT_STATUS).find((e) => e[1] === data.status)[0]
            : undefined,
          media: data.media,
          infoMedia: data.infoMedia,
          registerStartAt: data.registerStartAt,
          registerEndAt: data.registerEndAt,
        },
        refetchQueries: [
          FindOneEvent,
          'FindOneEvent',
        ],
      });
    }
  };

  return (
    <View style={styles.containerFlex}>
      {oneEventQueryLoading && <LoadingIndicator text="Chargement de l'événement..." />}
      {oneEventQueryError && (
        <View style={styles.Middle}>
          <Text>{oneEventQueryError.message}</Text>
        </View>
      )}
      {displayCreateAchievementModal.display && <CreateAchievementModal />}
      {displayManageTripModal.display && <ManageTripModal eventId={event?.id} />}
      {event ? (
        <>
          <Card>
            {event.media ? (
              <Image
                style={{
                  alignItems: 'center',
                  backgroundColor: 'transparent',
                  width: '100%',
                  height: 200,
                  resizeMode: 'cover', // Add resizeMode for better fit
                }}
                source={{ uri: event.media }}
              />
            ) : null}
            {isSuperAdmin ? (
              <Card.Actions>
                <Menu
                  visible={visibleEventType}
                  anchorPosition="bottom"
                  onDismiss={() => setVisibleEventType(!visibleEventType)}
                  anchor={(
                    <Chip closeIcon="application-edit" onClose={() => setVisibleEventType(!visibleEventType)}>
                      {EVENT_TYPE[event.type]}
                    </Chip>
                  )}
                >
                  {Object.entries(EVENT_TYPE).filter((e) => e[0] !== event.type)
                    .map((e) => (
                      <Menu.Item
                        key={e[0]}
                        onPress={() => saveEventCallback({ type: e[1] })}
                        title={e[1]}
                      />
                    ))}
                </Menu>
                <Menu
                  visible={visibleEventLevel}
                  anchorPosition="bottom"
                  onDismiss={() => setVisibleEventLevel(!visibleEventLevel)}
                  anchor={(
                    <Chip closeIcon="application-edit" onClose={() => setVisibleEventLevel(!visibleEventLevel)}>
                      {EVENT_LEVEL[event.level]}
                    </Chip>
                  )}
                >
                  {Object.entries(EVENT_LEVEL).filter((e) => e[0] !== event.level)
                    .map((e) => (
                      <Menu.Item
                        key={e[0]}
                        onPress={() => saveEventCallback({
                          level: e[1],
                          registerEndAt: addDays(
                            new Date(event.startAt),
                            e[1] === EVENT_LEVEL.REGIONAL ? -15 : -30,
                          ),
                        })}
                        title={e[1]}
                      />
                    ))}
                </Menu>
                <Menu
                  visible={visibleEventStatus}
                  anchorPosition="bottom"
                  onDismiss={() => setVisibleEventStatus(!visibleEventStatus)}
                  anchor={(
                    <Chip closeIcon="application-edit" onClose={() => setVisibleEventStatus(!visibleEventStatus)}>
                      {EVENT_STATUS[event.status]}
                    </Chip>
                  )}
                >
                  {Object.entries(EVENT_STATUS).filter((e) => e[0] !== event.status)
                    .map((e) => (
                      <Menu.Item
                        key={e[0]}
                        onPress={() => saveEventCallback({ status: e[1] })}
                        title={e[1]}
                      />
                    ))}
                </Menu>
                <IconButton
                  icon="image-edit"
                  size={20}
                  onPress={async () => {
                    const result = await ImagePicker.launchImageLibraryAsync({
                      mediaTypes: ImagePicker.MediaTypeOptions.Images,
                      allowsEditing: true,
                      aspect: [460, 200],
                      quality: 1,
                      base64: true,
                    });
                    if (!result.canceled) {
                      const media = Platform.OS === 'web' ? result.assets[0].uri : `data:${result.assets[0].mimeType};base64,${result.assets[0].base64}`;
                      saveEventCallback({ media });
                    }
                  }}
                />
                <IconButton
                  icon="note-edit"
                  size={20}
                  onPress={async () => {
                    const result = await ImagePicker.launchImageLibraryAsync({
                      mediaTypes: ImagePicker.MediaTypeOptions.Images,
                      allowsEditing: true,
                      aspect: [9, 16],
                      quality: 1,
                      base64: true,
                    });
                    if (!result.canceled) {
                      const infoMedia = Platform.OS === 'web' ? result.assets[0].uri : `data:${result.assets[0].mimeType};base64,${result.assets[0].base64}`;
                      saveEventCallback({ infoMedia });
                    }
                  }}
                />
              </Card.Actions>
            ) : (
              <Card.Actions>
                <Chip>
                  {EVENT_TYPE[event.type]}
                </Chip>
                <Chip>
                  {EVENT_LEVEL[event.level]}
                </Chip>
                <Chip>
                  {EVENT_STATUS[event.status]}
                </Chip>
              </Card.Actions>
            )}
          </Card>
          <EventTabs event={event} disabled={!isSuperAdmin} />
        </>
      ) : null}
    </View>
  );
}

export default function EventScreen() {
  return (
    <NativeBaseProvider>
      <Event />
    </NativeBaseProvider>
  );
}
